// A CSS reset and shared styles that are used across our app
import "../styles/shared.css";

import {t} from "@lingui/core/macro";
import {useMemo} from "react";
import {Helmet, HelmetProvider} from "react-helmet-async";

import {RenderStateRoot} from "@khanacademy/wonder-blocks-core";
import {
    MEDIA_DEFAULT_SPEC,
    MediaLayoutContext,
    type MediaSize,
    type MediaSpec,
} from "@khanacademy/wonder-blocks-layout";
import {GqlRouter} from "@khan/data-gql";
import {LinguiI18nProvider} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {AppShellContext, type Entrypoint} from "@khan/shared-core/context";

import {ExternalStylesMissing} from "./external-styles-missing.tsx";
import GrowthBookProvider from "./growthbook-provider.tsx";
import {UnsupportedBrowser} from "./unsupported-browser.tsx";
import {useFlagsQueryParamToCookie} from "./use-flags-query-param-to-cookie.ts";
import {useTrackPagePerformance} from "./use-track-page-performance.ts";

interface Props {
    children: React.ReactNode;

    /* The name of the entrypoint.  Used by app-shell-package/errors/not-found.tsx
       to do a full page reload when navigating between entrypoints in the
       same domain. */
    entrypoint: Entrypoint;
}

const mediaLayout: {ssrSize: MediaSize; mediaSpec: MediaSpec} = {
    ssrSize: RequestInfo.isPhone ? "small" : "large",
    mediaSpec: MEDIA_DEFAULT_SPEC,
};

export default function AppShell({entrypoint, children}: Props) {
    useTrackPagePerformance(entrypoint);
    useFlagsQueryParamToCookie();

    const appShellContext = useMemo(
        () => ({
            inAppShell: true,
            entrypoint,
        }),
        [entrypoint],
    );

    return (
        <HelmetProvider>
            <Helmet
                titleTemplate={
                    RequestInfo.IS_DEV_SERVER
                        ? `[DEV] %s | ${t`Khan Academy`}`
                        : `%s | ${t`Khan Academy`}`
                }
            >
                <title>{t`Khan Academy`}</title>
            </Helmet>
            <LinguiI18nProvider>
                <MediaLayoutContext.Provider value={mediaLayout}>
                    <RenderStateRoot throwIfNested={false}>
                        <AppShellContext.Provider value={appShellContext}>
                            <GrowthBookProvider>
                                <GqlRouter>
                                    <UnsupportedBrowser />
                                    <ExternalStylesMissing />
                                    {children}
                                </GqlRouter>
                            </GrowthBookProvider>
                        </AppShellContext.Provider>
                    </RenderStateRoot>
                </MediaLayoutContext.Provider>
            </LinguiI18nProvider>
        </HelmetProvider>
    );
}
